import { AuthClient } from "@gbougakov/brief-ts";
import type {
  AuthSuccessResponse,
  RegistrationVerifyRequest,
  AuthenticationVerifyRequest,
} from "@gbougakov/brief-ts";
import { BRIEF_ENDPOINT } from "./constants";

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

const authClient = new AuthClient(BRIEF_ENDPOINT);

export async function authenticate(): Promise<AuthSuccessResponse> {
  const opts = await authClient.getAuthenticationOptions();

  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: base64urlToBuffer(opts.challenge),
      rpId: (opts as any).rpId ?? "brief.werknaam.eu",
      timeout: (opts as any).timeout ?? 60000,
      userVerification: "required",
    },
  })) as PublicKeyCredential & {
    response: AuthenticatorAssertionResponse;
  };

  const body: AuthenticationVerifyRequest = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    response: {
      authenticatorData: bufferToBase64url(credential.response.authenticatorData),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      signature: bufferToBase64url(credential.response.signature),
      userHandle: credential.response.userHandle
        ? bufferToBase64url(credential.response.userHandle)
        : undefined,
    },
    type: "public-key",
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment:
      (credential as any).authenticatorAttachment ?? "platform",
  };

  return authClient.verifyAuthentication(body);
}

export async function register(): Promise<AuthSuccessResponse> {
  const opts = await authClient.getRegistrationOptions();

  const credential = (await navigator.credentials.create({
    publicKey: {
      challenge: base64urlToBuffer(opts.challenge),
      rp: opts.rp,
      user: {
        id: base64urlToBuffer(opts.user.id),
        name: opts.user.name,
        displayName: opts.user.displayName,
      },
      pubKeyCredParams: opts.pubKeyCredParams as PublicKeyCredentialParameters[],
      timeout: opts.timeout,
      attestation: opts.attestation as AttestationConveyancePreference,
      authenticatorSelection:
        opts.authenticatorSelection as AuthenticatorSelectionCriteria,
    },
  })) as PublicKeyCredential & {
    response: AuthenticatorAttestationResponse;
  };

  const body: RegistrationVerifyRequest = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    response: {
      attestationObject: bufferToBase64url(
        credential.response.attestationObject,
      ),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
    },
    type: "public-key",
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment:
      (credential as any).authenticatorAttachment ?? "platform",
  };

  return authClient.verifyRegistration(body);
}

export { authClient };
